import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import Table, { TableColumn } from "../../../components/table/Table";
import Avatar from "../../../components/avatar/Avatar";
import IconButton from "../../../components/button/IconButton";
import DropdownMenu, {
  DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import SvgLoadingAnimation from "../../../components/loading";
import ToolTip from "../../../components/tooltip/ToolTip";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import {
  useAudioAudition,
  type AuditionItem,
} from "../../../components/audio-preview/core/useAudioAudition";
import AudioInspectorBar, {
  AUDIO_INSPECTOR_BAR_HEIGHT,
  formatPreciseTime,
  type AudioInspectorBookmark,
  type AudioInspectorFile,
  type AudioInspectorFocusRequest,
} from "../../../components/audio-preview/AudioInspectorBar";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import {
  useGetLibrarySectionBookmarksQuery,
  useCreateLibraryBookmarkMutation,
  useCreateLibraryBookmarkFolderMutation,
  useUpdateLibraryBookmarkFolderMutation,
  useDeleteLibraryBookmarkFolderMutation,
  useUpdateLibraryBookmarkMutation,
  useDeleteLibraryBookmarkMutation,
  type LibraryBookmarkFolderItem,
  type LibraryFileItem,
  type LibrarySection,
  type LibrarySectionBookmark,
  type LibraryUserBrief,
} from "../../../app/services/library";

/**
 * サイドバーの「ブックマーク」ページ本体。
 *
 * オーディオインスペクタで付けたチーム共有ブックマークを、ファイルリストと
 * 同じ表形式（グローバル Table）で「作成者ユーザー > ブックマーク > ファイル」
 * の構成で表示する。
 *
 * - 行頭の再生ボタン（または行クリック）でページ内にインスペクタを開き、
 *   ブックマーク位置へ頭出し（範囲なら選択も復元）して再生できる
 * - ブックマーク名はクリックでその場で編集（権限がある場合）
 * - ファイル名リンクのクリックで該当ファイルの場所（ファイルリスト）へ移動
 * - 各ユーザーのツリーはファイルリスト同様にフォルダで整理できる
 *   （作成・D&D 移動・リネーム・削除。自分のツリー or エディター以上）
 */

interface LibraryBookmarkListProps {
  section: LibrarySection;
  /** ファイル名リンクから、ファイルの場所（ファイルリスト）へ移動する */
  // eslint-disable-next-line no-unused-vars
  onJump: (bookmark: LibrarySectionBookmark) => void;
  /** 他の固定バー（BottomAppBar 等）と共存する場合の下端オフセット（px） */
  audioInspectorBottomOffset?: number;
}

// ===== ツリー構造 =====

interface TreeFolder {
  folder: LibraryBookmarkFolderItem;
  children: TreeFolder[];
  bookmarks: LibrarySectionBookmark[];
  /** 配下（子孫含む）の総ブックマーク数 */
  totalCount: number;
  depth: number;
}

interface UserGroup {
  /** user id 文字列。作成者不明は "unknown" */
  key: string;
  user: LibraryUserBrief | null;
  rootFolders: TreeFolder[];
  rootBookmarks: LibrarySectionBookmark[];
  totalCount: number;
}

/** ドラッグ中のアイテム（ブックマーク or フォルダ） */
type ActiveDrag =
  | { kind: "bookmark"; bookmark: LibrarySectionBookmark; groupKey: string }
  | {
      kind: "folder";
      folder: LibraryBookmarkFolderItem;
      groupKey: string;
      /** 自分自身と配下の全フォルダID（循環防止のドロップ不可判定用） */
      excludedIds: Set<string>;
    };

const groupKeyOf = (user: LibraryUserBrief | null): string =>
  user ? String(user.id) : "unknown";

/** フォルダ自身と子孫の ID 集合（循環防止・削除確認用） */
const collectTreeFolderIds = (node: TreeFolder): Set<string> => {
  const ids = new Set<string>([node.folder.id]);
  const walk = (n: TreeFolder): void => {
    n.children.forEach((child) => {
      ids.add(child.folder.id);
      walk(child);
    });
  };
  walk(node);
  return ids;
};

// ===== Styled =====

const ListWrapper = styled.div<{ $bottomSpacing: number }>`
  width: 100%;
  /* インスペクタバーは fixed で浮くため、末尾の行が隠れないよう下に逃がす */
  padding: 8px 0 ${({ $bottomSpacing }) => 32 + $bottomSpacing}px;
`;

/** ファイルリスト（FileBrowser）と同じカード + 横スクロールの器 */
/** 作成者ユーザーの見出しセル（行全体を使う） */
const GroupHeaderCell = styled.td`
  && {
    padding: 12px 8px 4px;
  }
`;

/** 名前セル内の横並び（インデントは td の paddingLeft で行う） */
const CellRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/** フォルダ行の名前部（クリック = 開閉） */
const FolderNameButton = styled.button.attrs({
  type: "button",
  className: "bodytext-13",
})`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 2px 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  .material-symbols-rounded {
    font-size: 18px;
    color: var(--soremo-light-gray);
    flex-shrink: 0;
  }

  .expand-icon {
    transition: transform 0.15s ease;
  }

  .expand-icon.expanded {
    transform: rotate(90deg);
  }
`;

/** 行頭の再生 / 停止ボタン（ファイルリストの試聴ボタンと同じ見た目） */
const PlayIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $disabled: boolean }>`
  font-size: 20px;
  flex-shrink: 0;
  color: var(--soremo-light-gray);
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.35 : 1)};
`;

/** ブックマーク名（ShotTable のショット名セルと同じ密度。クリックでその場編集） */
const NameText = styled.div.attrs({ className: "bodytext-13" })<{
  $editable: boolean;
}>`
  flex: 1;
  min-width: 0;
  min-height: 22px;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${({ $editable }) => ($editable ? "text" : "default")};
`;

/**
 * セル内編集用 input の高密度スタイル（ShotTable のショット名編集と同じ流儀）。
 * 枠は持たず、編集中の表示はセル側の td:focus-within リングに任せる。
 * inline style なのは、グローバル input の :focus 指定
 * （font-size 16px / box-shadow）へ確実に勝たせてセルサイズを変えないため。
 */
const NAME_INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: "auto",
  height: 22,
  padding: 0,
  fontSize: 13,
  lineHeight: "22px",
  border: "none",
  borderRadius: 0,
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/** ブックマーク行のファイルリンク（クリックでファイルの場所へ移動） */
const FileLink = styled.span.attrs({ className: "bodytext-13" })`
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
    }
  }
`;

const MetaCell = styled.td.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
`;

const ActionsCell = styled.td`
  /* DropdownMenu(Tippy) がセル内でクリップされないようにする */
  overflow: visible !important;
  text-align: center;
`;

const EmptyState = styled.div.attrs({ className: "u-col-center u-w100" })`
  padding: 48px 0;
  gap: 8px;
`;

const RenameInput = styled.input.attrs({ type: "text" })`
  width: 100%;
`;

// D&D 中にカーソルへ追従するゴースト表示（FileBrowser と同じ見た目）
const DragGhost = styled.div.attrs({ className: "bodytext-13" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid var(--soremo-blue);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

// ===== ヘルパー =====

const formatBookmarkTime = (bookmark: LibrarySectionBookmark): string => {
  const start = formatPreciseTime(bookmark.start_seconds);
  if (bookmark.end_seconds == null) return start;
  const length = (bookmark.end_seconds - bookmark.start_seconds).toFixed(2);
  return `${start} – ${formatPreciseTime(bookmark.end_seconds)}（${length} 秒）`;
};

const bookmarkDisplayName = (bookmark: LibrarySectionBookmark): string =>
  bookmark.label || formatBookmarkTime(bookmark);

/** ドラッグ中アイテムをこのフォルダへドロップできるか */
const isValidFolderDrop = (
  drag: ActiveDrag | null,
  node: TreeFolder,
  groupKey: string,
): boolean => {
  if (!drag || drag.groupKey !== groupKey) return false;
  if (drag.kind === "bookmark") {
    return drag.bookmark.folder_id !== node.folder.id;
  }
  return (
    !drag.excludedIds.has(node.folder.id) &&
    drag.folder.parent_id !== node.folder.id
  );
};

/** ドラッグ中アイテムをこのグループのツリー直下へ戻せるか */
const isValidRootDrop = (
  drag: ActiveDrag | null,
  groupKey: string,
): boolean => {
  if (!drag || drag.groupKey !== groupKey) return false;
  if (drag.kind === "bookmark") return drag.bookmark.folder_id !== null;
  return drag.folder.parent_id !== null;
};

/** ドロップ受け入れ状態の行ハイライト（FileBrowser のフォルダ行と同じ） */
const dropHighlightStyle = (
  validTarget: boolean,
  isOver: boolean,
): React.CSSProperties =>
  validTarget
    ? {
        background: isOver ? "rgba(0, 154, 206, 0.10)" : undefined,
        boxShadow: isOver
          ? "inset 0 0 0 2px var(--soremo-blue)"
          : "inset 0 0 0 1px rgba(0, 154, 206, 0.3)",
      }
    : {};

// ===== 行コンポーネント =====

/** グループ見出し行（ツリー直下へのドロップ先を兼ねる） */
const GroupHeaderRow: React.FC<{
  group: UserGroup;
  activeDrag: ActiveDrag | null;
  canCreateFolder: boolean;
  onCreateFolder: () => void;
}> = ({ group, activeDrag, canCreateFolder, onCreateFolder }) => {
  const validTarget = isValidRootDrop(activeDrag, group.key);
  const { setNodeRef, isOver } = useDroppable({
    id: `bm-root-drop:${group.key}`,
    data: { type: "root", groupKey: group.key },
    disabled: !validTarget,
  });
  return (
    <tr ref={setNodeRef} style={dropHighlightStyle(validTarget, isOver)}>
      <GroupHeaderCell colSpan={4}>
        <div className="u-row u-gap8">
          <Avatar
            size={24}
            src={group.user?.avatar ?? undefined}
            name={group.user?.display_name}
          />
          <span className="heading-13 u-line-height-150 u-text-ellipsis">
            {group.user?.display_name ?? "不明なユーザー"}
          </span>
          <span className="bodytext-11 u-text-light-gray u-flex-none">
            {group.totalCount}件
          </span>
          <span style={{ flex: 1 }} />
          {canCreateFolder && (
            <ToolTip description="フォルダを作成">
              <IconButton
                icon="create_new_folder"
                size={20}
                color="var(--soremo-light-gray)"
                onClick={onCreateFolder}
                ariaLabel={`${group.user?.display_name ?? ""}のツリーにフォルダを作成`}
              />
            </ToolTip>
          )}
        </div>
      </GroupHeaderCell>
    </tr>
  );
};

/** フォルダ行（ドラッグ元 + ドロップ先） */
const FolderRow: React.FC<{
  node: TreeFolder;
  groupKey: string;
  isExpanded: boolean;
  draggable: boolean;
  activeDrag: ActiveDrag | null;
  onToggle: (_folderId: string) => void;
  menuItems: DropdownMenuItem[];
}> = ({
  node,
  groupKey,
  isExpanded,
  draggable,
  activeDrag,
  onToggle,
  menuItems,
}) => {
  const validTarget = isValidFolderDrop(activeDrag, node, groupKey);
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `bm-folder-drop:${node.folder.id}`,
    data: { type: "folder", folderId: node.folder.id },
    disabled: !validTarget,
  });
  const excludedIds = useMemo(() => collectTreeFolderIds(node), [node]);
  const dragData = useMemo<ActiveDrag>(
    () => ({ kind: "folder", folder: node.folder, groupKey, excludedIds }),
    [node.folder, groupKey, excludedIds],
  );
  const {
    setNodeRef: setDragRef,
    listeners,
    attributes,
    isDragging,
  } = useDraggable({
    id: `bm-folder:${node.folder.id}`,
    data: dragData,
    disabled: !draggable,
  });

  return (
    <tr
      ref={(el: HTMLTableRowElement | null) => {
        setDropRef(el);
        setDragRef(el);
      }}
      {...listeners}
      {...attributes}
      style={{
        opacity: isDragging ? 0.4 : 1,
        touchAction: draggable ? "none" : undefined,
        outline: "none",
        ...dropHighlightStyle(validTarget, isOver),
      }}
    >
      <td style={{ paddingLeft: 8 + node.depth * 20 }}>
        <FolderNameButton onClick={() => onToggle(node.folder.id)}>
          <span
            className={`material-symbols-rounded expand-icon${isExpanded ? " expanded" : ""}`}
          >
            chevron_right
          </span>
          <span className="material-symbols-rounded">folder</span>
          <span className="u-text-ellipsis">{node.folder.name}</span>
          <span className="bodytext-11 u-text-light-gray u-flex-none">
            {node.totalCount}件
          </span>
        </FolderNameButton>
      </td>
      <td />
      <MetaCell>
        <DateDisplay
          date={node.folder.updated_at}
          type={DATE_DISPLAY_TYPE.DATE_TIME}
        />
      </MetaCell>
      <ActionsCell>
        {menuItems.length > 0 && <DropdownMenu items={menuItems} />}
      </ActionsCell>
    </tr>
  );
};

/** ブックマーク行（ドラッグ元。再生ボタン + 名前インライン編集 + ファイルリンク） */
const BookmarkRow: React.FC<{
  bookmark: LibrarySectionBookmark;
  groupKey: string;
  depth: number;
  draggable: boolean;
  /** 音源が見つかりページ内で再生できるか */
  playable: boolean;
  /** インスペクタの対象ブックマークか（行ハイライト） */
  isActive: boolean;
  /** このブックマークを再生中か（pause アイコン表示） */
  isPlaying: boolean;
  isEditing: boolean;
  editDraft: string;
  canEditName: boolean;
  onEditDraftChange: (_value: string) => void;
  onStartEdit: (_bookmark: LibrarySectionBookmark) => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  /** 選択 + 頭出し（play = true なら再生も開始 / 再生中なら停止） */
  onFocusBookmark: (_bookmark: LibrarySectionBookmark, _play: boolean) => void;
  onJumpToFile: (_bookmark: LibrarySectionBookmark) => void;
  menuItems: DropdownMenuItem[];
}> = ({
  bookmark,
  groupKey,
  depth,
  draggable,
  playable,
  isActive,
  isPlaying,
  isEditing,
  editDraft,
  canEditName,
  onEditDraftChange,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onFocusBookmark,
  onJumpToFile,
  menuItems,
}) => {
  const dragData = useMemo<ActiveDrag>(
    () => ({ kind: "bookmark", bookmark, groupKey }),
    [bookmark, groupKey],
  );
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `bm-bookmark:${bookmark.id}`,
    data: dragData,
    disabled: !draggable,
  });

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => playable && onFocusBookmark(bookmark, false)}
      style={{
        opacity: isDragging ? 0.4 : 1,
        touchAction: draggable ? "none" : undefined,
        outline: "none",
        background: isActive ? "rgba(0, 154, 206, 0.06)" : undefined,
      }}
    >
      <td style={{ paddingLeft: 8 + depth * 20 }}>
        <CellRow>
          <PlayIcon
            $disabled={!playable}
            style={isPlaying ? { color: "var(--soremo-blue)" } : undefined}
            role="button"
            aria-label={
              isPlaying
                ? "停止"
                : `${bookmarkDisplayName(bookmark)} を再生`
            }
            onClick={(e) => {
              e.stopPropagation();
              if (playable) onFocusBookmark(bookmark, true);
            }}
          >
            {isPlaying ? "pause_circle" : "play_circle"}
          </PlayIcon>
          {isEditing ? (
            <input
              type="text"
              value={editDraft}
              autoFocus
              placeholder={formatBookmarkTime(bookmark)}
              style={NAME_INPUT_STYLE}
              onChange={(e) => onEditDraftChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={onCommitEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  onCommitEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  onCancelEdit();
                }
              }}
            />
          ) : (
            <NameText
              $editable={canEditName}
              onClick={
                canEditName
                  ? (e) => {
                      e.stopPropagation();
                      onStartEdit(bookmark);
                    }
                  : undefined
              }
            >
              {bookmarkDisplayName(bookmark)}
            </NameText>
          )}
        </CellRow>
      </td>
      <td>
        <FileLink
          onClick={(e) => {
            e.stopPropagation();
            onJumpToFile(bookmark);
          }}
        >
          {bookmark.file.name}
        </FileLink>
        <span className="bodytext-11 u-text-light-gray">
          {` ・ ${formatBookmarkTime(bookmark)}`}
        </span>
      </td>
      <MetaCell>
        <DateDisplay
          date={bookmark.created_at}
          type={DATE_DISPLAY_TYPE.DATE_TIME}
        />
      </MetaCell>
      <ActionsCell>
        {menuItems.length > 0 && <DropdownMenu items={menuItems} />}
      </ActionsCell>
    </tr>
  );
};

// ===== 本体 =====

const LibraryBookmarkList: React.FC<LibraryBookmarkListProps> = ({
  section,
  onJump,
  audioInspectorBottomOffset = 0,
}) => {
  const { data, isLoading } = useGetLibrarySectionBookmarksQuery({
    section_id: section.id,
  });
  const { data: currentUserData } = useGetCurrentUserQuery(undefined);
  const myId: number | null = currentUserData?.data?.id ?? null;

  const [createFolder, { isLoading: isCreatingFolder }] =
    useCreateLibraryBookmarkFolderMutation();
  const [updateFolder, { isLoading: isRenamingFolder }] =
    useUpdateLibraryBookmarkFolderMutation();
  const [deleteFolder, { isLoading: isDeletingFolder }] =
    useDeleteLibraryBookmarkFolderMutation();
  const [createBookmark] = useCreateLibraryBookmarkMutation();
  const [updateBookmark] = useUpdateLibraryBookmarkMutation();
  const [deleteBookmark] = useDeleteLibraryBookmarkMutation();

  // 開いているフォルダ（既定は全部閉じる。少数運用を想定）
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  // フォルダ作成モーダル（parentId = null はツリー直下）
  const [createFolderState, setCreateFolderState] = useState<{
    parentId: string | null;
    parentName?: string;
  } | null>(null);
  const [createFolderName, setCreateFolderName] = useState("");
  // フォルダのリネームモーダル（ブックマーク名は行内で直接編集する）
  const [renameFolderTarget, setRenameFolderTarget] =
    useState<LibraryBookmarkFolderItem | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  // フォルダ削除確認
  const [deleteFolderTarget, setDeleteFolderTarget] =
    useState<TreeFolder | null>(null);
  // ブックマーク名のインライン編集
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(
    null,
  );
  const [editDraft, setEditDraft] = useState("");

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const bookmarks = useMemo(
    () => data?.data.bookmarks ?? [],
    [data],
  );

  // ===== グループ / ツリー構築 =====

  const groups = useMemo<UserGroup[]>(() => {
    const folders = data?.data.folders ?? [];

    const groupMap = new Map<string, UserGroup>();
    const ensureGroup = (user: LibraryUserBrief | null): UserGroup => {
      const key = groupKeyOf(user);
      let group = groupMap.get(key);
      if (!group) {
        group = {
          key,
          user,
          rootFolders: [],
          rootBookmarks: [],
          totalCount: 0,
        };
        groupMap.set(key, group);
      }
      return group;
    };

    // フォルダノードを作成者（owner）ごとにツリー化する
    const nodesById = new Map<string, TreeFolder>();
    folders.forEach((folder) => {
      nodesById.set(folder.id, {
        folder,
        children: [],
        bookmarks: [],
        totalCount: 0,
        depth: 0,
      });
    });
    folders.forEach((folder) => {
      const node = nodesById.get(folder.id)!;
      const parent = folder.parent_id
        ? nodesById.get(folder.parent_id)
        : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        ensureGroup(folder.owner).rootFolders.push(node);
      }
    });

    // ブックマークを所属フォルダ（無ければツリー直下）へ配る
    bookmarks.forEach((bookmark) => {
      const group = ensureGroup(bookmark.created_by);
      const node = bookmark.folder_id
        ? nodesById.get(bookmark.folder_id)
        : undefined;
      if (node) {
        node.bookmarks.push(bookmark);
      } else {
        group.rootBookmarks.push(bookmark);
      }
      group.totalCount += 1;
    });

    // depth と配下の総数を確定する
    const finalize = (node: TreeFolder, depth: number): number => {
      node.depth = depth;
      node.totalCount =
        node.bookmarks.length +
        node.children.reduce(
          (sum, child) => sum + finalize(child, depth + 1),
          0,
        );
      return node.totalCount;
    };
    groupMap.forEach((group) => {
      group.rootFolders.forEach((node) => finalize(node, 0));
    });

    // 自分 → 名前順 → 不明ユーザーの順に並べる
    return [...groupMap.values()].sort((a, b) => {
      const aMe = a.user?.id === myId ? 0 : 1;
      const bMe = b.user?.id === myId ? 0 : 1;
      if (aMe !== bMe) return aMe - bMe;
      if (!a.user) return 1;
      if (!b.user) return -1;
      return a.user.display_name.localeCompare(b.user.display_name, "ja");
    });
  }, [data, bookmarks, myId]);

  const foldersById = useMemo(() => {
    const map = new Map<string, LibraryBookmarkFolderItem>();
    (data?.data.folders ?? []).forEach((f) => map.set(f.id, f));
    return map;
  }, [data]);

  // ===== ページ内試聴（インスペクタ再生） =====

  // ブックマーク対象ファイルのフル情報（URL・波形含む）は一覧 API が
  // files として同梱する（ツリー API はファイルを返さないため）。
  const sectionFilesById = useMemo(() => {
    const map = new Map<string, LibraryFileItem>();
    (data?.data.files ?? []).forEach((f) => map.set(f.id, f));
    return map;
  }, [data]);

  // ブックマークが付いたオーディオを、リストの表示順で試聴対象にする
  // （インスペクタの上下キーによる前後移動も表示順どおりになる）
  const auditionItems = useMemo<AuditionItem[]>(() => {
    const seen = new Set<string>();
    const items: AuditionItem[] = [];
    const add = (bookmark: LibrarySectionBookmark): void => {
      if (seen.has(bookmark.file.id)) return;
      const file = sectionFilesById.get(bookmark.file.id);
      if (!file || file.file_type !== "audio") return;
      const src = file.optimized_url || file.original_url;
      if (!src) return;
      seen.add(bookmark.file.id);
      items.push({ key: bookmark.file.id, src });
    };
    const walk = (node: TreeFolder): void => {
      node.children.forEach(walk);
      node.bookmarks.forEach(add);
    };
    groups.forEach((group) => {
      group.rootFolders.forEach(walk);
      group.rootBookmarks.forEach(add);
    });
    return items;
  }, [groups, sectionFilesById]);

  const auditionKeys = useMemo(
    () => new Set(auditionItems.map((item) => item.key)),
    [auditionItems],
  );

  const audition = useAudioAudition(auditionItems);

  // インスペクタの対象ブックマーク（行ハイライト・pause 表示用）
  const [activeBookmarkId, setActiveBookmarkId] = useState<string | null>(
    null,
  );
  // 閉じるボタンで一旦引っ込め、別のブックマークを選んだらまた出す
  const [inspectorDismissed, setInspectorDismissed] = useState(false);
  const [inspectorFocus, setInspectorFocus] =
    useState<AudioInspectorFocusRequest | null>(null);
  const focusCounterRef = useRef(0);

  useEffect(() => {
    setInspectorDismissed(false);
  }, [audition.selectedKey]);

  const inspectorFile = useMemo<AudioInspectorFile | null>(() => {
    if (inspectorDismissed || !audition.selectedKey) return null;
    const file = sectionFilesById.get(audition.selectedKey);
    if (!file?.original_url) return null;
    return {
      id: file.id,
      name: file.file_name,
      originalUrl: file.original_url,
      optimizedUrl: file.optimized_url,
      waveformUrl: file.waveform_url,
    };
  }, [inspectorDismissed, audition.selectedKey, sectionFilesById]);

  // インスペクタの波形に重ねる、表示中ファイルのブックマーク
  const inspectorBookmarks = useMemo<AudioInspectorBookmark[]>(() => {
    if (!inspectorFile) return [];
    return bookmarks
      .filter((b) => b.file.id === inspectorFile.id)
      .map((b) => ({
        id: b.id,
        startSeconds: b.start_seconds,
        endSeconds: b.end_seconds,
        label: b.label,
        createdByName: b.created_by?.display_name ?? null,
        createdByAvatar: b.created_by?.avatar ?? null,
      }));
  }, [bookmarks, inspectorFile]);

  /**
   * ブックマークを選択してインスペクタで頭出しする。
   * play = true: 再生も開始（すでにそのブックマークを再生中なら停止）
   * play = false: 行クリック。選択と頭出しだけ行う
   */
  const focusBookmark = useCallback(
    (bookmark: LibrarySectionBookmark, play: boolean) => {
      const fileId = bookmark.file.id;
      if (!auditionKeys.has(fileId)) return;
      if (
        play &&
        activeBookmarkId === bookmark.id &&
        audition.playingKey === fileId
      ) {
        audition.pause();
        return;
      }
      setActiveBookmarkId(bookmark.id);
      setInspectorDismissed(false);
      focusCounterRef.current += 1;
      setInspectorFocus({
        requestId: focusCounterRef.current,
        startSeconds: bookmark.start_seconds,
        endSeconds: bookmark.end_seconds,
      });
      if (audition.selectedKey !== fileId) {
        // 別ファイル: 選択して（play なら続けて自動再生）
        if (play) {
          audition.toggle(fileId);
        } else {
          audition.select(fileId);
        }
      } else if (play && !audition.isPlaying) {
        audition.toggle(fileId);
      }
    },
    [auditionKeys, activeBookmarkId, audition],
  );

  const handleFocusRequestApplied = useCallback(
    () => setInspectorFocus(null),
    [],
  );

  // インスペクタからのブックマーク操作（追加は B キー / ブックマークボタン）
  const handleInspectorCreateBookmark = useCallback(
    async (params: { startSeconds: number; endSeconds: number | null }) => {
      if (!inspectorFile) return;
      try {
        // 追加トーストは出さない（波形ピン / 一覧への反映で分かるため）
        await createBookmark({
          file_id: inspectorFile.id,
          start_seconds: params.startSeconds,
          end_seconds: params.endSeconds,
        }).unwrap();
      } catch {
        toast.error("ブックマークを追加できませんでした");
      }
    },
    [inspectorFile, createBookmark],
  );

  const handleInspectorUpdateBookmark = useCallback(
    async (bookmarkId: string, patch: { label: string }) => {
      if (!inspectorFile) return;
      try {
        await updateBookmark({
          bookmark_id: bookmarkId,
          file_id: inspectorFile.id,
          label: patch.label,
        }).unwrap();
      } catch {
        toast.error("ブックマークを更新できませんでした");
      }
    },
    [inspectorFile, updateBookmark],
  );

  const handleInspectorDeleteBookmark = useCallback(
    async (bookmarkId: string) => {
      if (!inspectorFile) return;
      try {
        await deleteBookmark({
          bookmark_id: bookmarkId,
          file_id: inspectorFile.id,
        }).unwrap();
      } catch {
        toast.error("ブックマークを削除できませんでした");
      }
    },
    [inspectorFile, deleteBookmark],
  );

  // ===== 権限 =====

  const canModifyBookmark = useCallback(
    (bookmark: LibrarySectionBookmark): boolean =>
      section.can_edit || (myId !== null && bookmark.created_by?.id === myId),
    [section.can_edit, myId],
  );

  const canModifyFolder = useCallback(
    (folder: LibraryBookmarkFolderItem): boolean =>
      section.can_edit || (myId !== null && folder.owner?.id === myId),
    [section.can_edit, myId],
  );

  /** フォルダ作成は自分のツリーのみ（API が owner = 自分で固定するため） */
  const isMyTree = useCallback(
    (owner: LibraryUserBrief | null): boolean =>
      myId !== null && owner?.id === myId,
    [myId],
  );

  // ===== 操作 =====

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const openCreateFolder = useCallback(
    (parentId: string | null, parentName?: string) => {
      setCreateFolderName("");
      setCreateFolderState({ parentId, parentName });
    },
    [],
  );

  const handleCreateFolderConfirm = useCallback(async () => {
    if (!createFolderState || isCreatingFolder) return;
    const name = createFolderName.trim();
    if (!name) return;
    try {
      await createFolder({
        section_id: section.id,
        name,
        ...(createFolderState.parentId
          ? { parent_id: createFolderState.parentId }
          : {}),
      }).unwrap();
      // 親フォルダの中に作った場合は開いて見えるようにする
      if (createFolderState.parentId) {
        setExpandedIds((prev) =>
          new Set(prev).add(createFolderState.parentId!),
        );
      }
      setCreateFolderState(null);
    } catch {
      toast.error("フォルダを作成できませんでした");
    }
  }, [
    createFolderState,
    createFolderName,
    isCreatingFolder,
    createFolder,
    section.id,
  ]);

  const openRenameFolder = useCallback((folder: LibraryBookmarkFolderItem) => {
    setRenameDraft(folder.name);
    setRenameFolderTarget(folder);
  }, []);

  const handleRenameFolderConfirm = useCallback(async () => {
    if (!renameFolderTarget) return;
    const name = renameDraft.trim();
    if (!name || name === renameFolderTarget.name) return;
    try {
      await updateFolder({
        folder_id: renameFolderTarget.id,
        name,
      }).unwrap();
      setRenameFolderTarget(null);
    } catch {
      toast.error("名前を変更できませんでした");
    }
  }, [renameFolderTarget, renameDraft, updateFolder]);

  // ブックマーク名のインライン編集（名前クリックで開始）
  const startInlineRename = useCallback(
    (bookmark: LibrarySectionBookmark) => {
      setEditDraft(bookmark.label);
      setEditingBookmarkId(bookmark.id);
    },
    [],
  );

  const cancelInlineRename = useCallback(() => {
    setEditingBookmarkId(null);
  }, []);

  const commitInlineRename = useCallback(async () => {
    if (!editingBookmarkId) return;
    const target = bookmarks.find((b) => b.id === editingBookmarkId);
    setEditingBookmarkId(null);
    if (!target) return;
    const label = editDraft.trim();
    if (label === target.label) return;
    try {
      await updateBookmark({
        bookmark_id: target.id,
        file_id: target.file.id,
        label,
      }).unwrap();
    } catch {
      toast.error("名前を変更できませんでした");
    }
  }, [editingBookmarkId, editDraft, bookmarks, updateBookmark]);

  const handleDeleteFolderConfirm = useCallback(async () => {
    if (!deleteFolderTarget || isDeletingFolder) return;
    try {
      await deleteFolder({
        folder_id: deleteFolderTarget.folder.id,
      }).unwrap();
      setDeleteFolderTarget(null);
    } catch {
      toast.error("フォルダを削除できませんでした");
    }
  }, [deleteFolderTarget, isDeletingFolder, deleteFolder]);

  const handleDeleteBookmark = useCallback(
    async (bookmark: LibrarySectionBookmark) => {
      try {
        await deleteBookmark({
          bookmark_id: bookmark.id,
          file_id: bookmark.file.id,
        }).unwrap();
        toast.success("ブックマークを削除しました");
      } catch {
        toast.error("ブックマークを削除できませんでした");
      }
    },
    [deleteBookmark],
  );

  const moveBookmark = useCallback(
    async (bookmark: LibrarySectionBookmark, folderId: string | null) => {
      const destName = folderId
        ? (foldersById.get(folderId)?.name ?? "フォルダ")
        : "一番上";
      try {
        await updateBookmark({
          bookmark_id: bookmark.id,
          file_id: bookmark.file.id,
          folder_id: folderId,
        }).unwrap();
        if (folderId) {
          setExpandedIds((prev) => new Set(prev).add(folderId));
        }
        toast.success(
          `「${bookmarkDisplayName(bookmark)}」を「${destName}」へ移動しました`,
        );
      } catch {
        toast.error("移動できませんでした");
      }
    },
    [updateBookmark, foldersById],
  );

  const moveFolder = useCallback(
    async (folder: LibraryBookmarkFolderItem, parentId: string | null) => {
      const destName = parentId
        ? (foldersById.get(parentId)?.name ?? "フォルダ")
        : "一番上";
      try {
        await updateFolder({
          folder_id: folder.id,
          parent_id: parentId,
        }).unwrap();
        if (parentId) {
          setExpandedIds((prev) => new Set(prev).add(parentId));
        }
        toast.success(`「${folder.name}」を「${destName}」へ移動しました`);
      } catch {
        toast.error("移動できませんでした");
      }
    },
    [updateFolder, foldersById],
  );

  // ===== D&D =====

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const drag = event.active.data.current as ActiveDrag | undefined;
    setActiveDrag(drag ?? null);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const drag = activeDrag;
      setActiveDrag(null);
      if (!drag || !event.over) return;
      const over = event.over.data.current as
        | { type: "folder"; folderId: string }
        | { type: "root"; groupKey: string }
        | undefined;
      if (!over) return;
      const destFolderId = over.type === "folder" ? over.folderId : null;
      if (drag.kind === "bookmark") {
        await moveBookmark(drag.bookmark, destFolderId);
      } else {
        await moveFolder(drag.folder, destFolderId);
      }
    },
    [activeDrag, moveBookmark, moveFolder],
  );

  // ===== メニュー =====

  const buildFolderMenuItems = useCallback(
    (node: TreeFolder): DropdownMenuItem[] => {
      if (!canModifyFolder(node.folder)) return [];
      const items: DropdownMenuItem[] = [];
      if (isMyTree(node.folder.owner)) {
        items.push({
          label: "フォルダを作成",
          icon: "create_new_folder",
          onClick: () => openCreateFolder(node.folder.id, node.folder.name),
        });
      }
      items.push({
        label: "リネーム",
        icon: "edit",
        onClick: () => openRenameFolder(node.folder),
      });
      if (node.folder.parent_id !== null) {
        items.push({
          label: "一番上へ移動",
          icon: "drive_file_move",
          onClick: () => void moveFolder(node.folder, null),
        });
      }
      items.push({
        label: "フォルダを削除",
        icon: "delete",
        onClick: () => setDeleteFolderTarget(node),
      });
      return items;
    },
    [canModifyFolder, isMyTree, openCreateFolder, openRenameFolder, moveFolder],
  );

  const buildBookmarkMenuItems = useCallback(
    (bookmark: LibrarySectionBookmark): DropdownMenuItem[] => {
      if (!canModifyBookmark(bookmark)) return [];
      const items: DropdownMenuItem[] = [
        {
          label: "リネーム",
          icon: "edit",
          onClick: () => startInlineRename(bookmark),
        },
      ];
      if (bookmark.folder_id !== null) {
        items.push({
          label: "一番上へ移動",
          icon: "drive_file_move",
          onClick: () => void moveBookmark(bookmark, null),
        });
      }
      items.push({
        label: "削除",
        icon: "delete",
        onClick: () => void handleDeleteBookmark(bookmark),
      });
      return items;
    },
    [canModifyBookmark, startInlineRename, moveBookmark, handleDeleteBookmark],
  );

  // ===== 描画 =====

  if (isLoading) return <SvgLoadingAnimation />;

  if (groups.length === 0) {
    return (
      <EmptyState>
        <span
          className="material-symbols-rounded"
          style={{ fontSize: "48px", color: "var(--soremo-light-gray)" }}
        >
          bookmark
        </span>
        <p className="bodytext-13 u-text-light-gray">
          ブックマークはまだありません
        </p>
        <p className="bodytext-11 u-text-light-gray">
          オーディオプレビューのブックマークボタン（または B キー）で追加できます
        </p>
      </EmptyState>
    );
  }

  const columns: TableColumn[] = [
    { key: "bookmark", header: "ブックマーク", minWidth: 200 },
    { key: "file", header: "ファイル", minWidth: 160 },
    { key: "created", header: "作成日", width: 140, minWidth: 100 },
    { key: "actions", width: 48, minWidth: 48, resizable: false },
  ];

  const renderBookmarkRow = (
    bookmark: LibrarySectionBookmark,
    group: UserGroup,
    depth: number,
  ): React.ReactNode => (
    <BookmarkRow
      key={bookmark.id}
      bookmark={bookmark}
      groupKey={group.key}
      depth={depth}
      draggable={
        canModifyBookmark(bookmark) && editingBookmarkId !== bookmark.id
      }
      playable={auditionKeys.has(bookmark.file.id)}
      isActive={activeBookmarkId === bookmark.id}
      isPlaying={
        activeBookmarkId === bookmark.id &&
        audition.playingKey === bookmark.file.id
      }
      isEditing={editingBookmarkId === bookmark.id}
      editDraft={editDraft}
      canEditName={canModifyBookmark(bookmark)}
      onEditDraftChange={setEditDraft}
      onStartEdit={startInlineRename}
      onCommitEdit={commitInlineRename}
      onCancelEdit={cancelInlineRename}
      onFocusBookmark={focusBookmark}
      onJumpToFile={onJump}
      menuItems={buildBookmarkMenuItems(bookmark)}
    />
  );

  const renderFolderNode = (
    node: TreeFolder,
    group: UserGroup,
  ): React.ReactNode => {
    const isExpanded = expandedIds.has(node.folder.id);
    return (
      <React.Fragment key={node.folder.id}>
        <FolderRow
          node={node}
          groupKey={group.key}
          isExpanded={isExpanded}
          draggable={canModifyFolder(node.folder)}
          activeDrag={activeDrag}
          onToggle={toggleFolder}
          menuItems={buildFolderMenuItems(node)}
        />
        {isExpanded && (
          <>
            {node.children.map((child) => renderFolderNode(child, group))}
            {node.bookmarks.map((bookmark) =>
              renderBookmarkRow(bookmark, group, node.depth + 1),
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <ListWrapper
      $bottomSpacing={
        inspectorFile
          ? AUDIO_INSPECTOR_BAR_HEIGHT + audioInspectorBottomOffset
          : 0
      }
    >
      <DndContext
        sensors={dndSensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
        <Table columns={columns} minWidth={560}>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              <GroupHeaderRow
                group={group}
                activeDrag={activeDrag}
                canCreateFolder={isMyTree(group.user)}
                onCreateFolder={() => openCreateFolder(null)}
              />
              {group.rootFolders.map((node) =>
                renderFolderNode(node, group),
              )}
              {group.rootBookmarks.map((bookmark) =>
                renderBookmarkRow(bookmark, group, 0),
              )}
            </React.Fragment>
          ))}
        </Table>

        {/* D&D 中にカーソルへ追従するゴースト */}
        <DragOverlay dropAnimation={null}>
          {activeDrag && (
            <DragGhost>
              <span className="material-symbols-rounded">
                {activeDrag.kind === "folder" ? "folder" : "bookmark"}
              </span>
              <span>
                {activeDrag.kind === "folder"
                  ? activeDrag.folder.name
                  : bookmarkDisplayName(activeDrag.bookmark)}
              </span>
            </DragGhost>
          )}
        </DragOverlay>
      </DndContext>

      {/* フォルダ作成モーダル */}
      {createFolderState && (
        <Modal
          title={
            createFolderState.parentName
              ? `「${createFolderState.parentName}」内にフォルダを作成`
              : "フォルダを作成"
          }
          titleSize="medium"
          onClose={() => setCreateFolderState(null)}
          primaryAction={{
            label: "作成",
            onClick: handleCreateFolderConfirm,
            loading: isCreatingFolder,
            disabled: !createFolderName.trim(),
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setCreateFolderState(null),
          }}
        >
          <RenameInput
            value={createFolderName}
            autoFocus
            placeholder="フォルダ名"
            onChange={(e) => setCreateFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void handleCreateFolderConfirm();
              }
            }}
          />
        </Modal>
      )}

      {/* フォルダのリネームモーダル */}
      {renameFolderTarget && (
        <Modal
          title="フォルダ名を変更"
          titleSize="medium"
          onClose={() => setRenameFolderTarget(null)}
          primaryAction={{
            label: "保存",
            onClick: handleRenameFolderConfirm,
            loading: isRenamingFolder,
            disabled:
              !renameDraft.trim() ||
              renameDraft.trim() === renameFolderTarget.name,
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setRenameFolderTarget(null),
          }}
        >
          <RenameInput
            value={renameDraft}
            autoFocus
            placeholder="フォルダ名"
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void handleRenameFolderConfirm();
              }
            }}
          />
        </Modal>
      )}

      {/* フォルダ削除確認ダイアログ（中身ごと削除） */}
      {deleteFolderTarget && (
        <ConfirmModal
          message={
            deleteFolderTarget.totalCount > 0
              ? `フォルダ「${deleteFolderTarget.folder.name}」を削除しますか？中のブックマーク ${deleteFolderTarget.totalCount}件もすべて削除されます`
              : `フォルダ「${deleteFolderTarget.folder.name}」を削除しますか？`
          }
          confirmLabel="削除"
          loading={isDeletingFolder}
          onConfirm={handleDeleteFolderConfirm}
          onCancel={() => setDeleteFolderTarget(null)}
        />
      )}

      {/* 選択中ブックマークの波形インスペクタ（ファイルリストと同じもの） */}
      {inspectorFile && (
        <AudioInspectorBar
          file={inspectorFile}
          transport={audition}
          onSelectPrev={audition.selectPrev}
          onSelectNext={audition.selectNext}
          bottomOffset={audioInspectorBottomOffset}
          onClose={() => setInspectorDismissed(true)}
          bookmarks={inspectorBookmarks}
          onCreateBookmark={handleInspectorCreateBookmark}
          onUpdateBookmark={handleInspectorUpdateBookmark}
          onDeleteBookmark={handleInspectorDeleteBookmark}
          focusRequest={inspectorFocus}
          onFocusRequestApplied={handleFocusRequestApplied}
        />
      )}
    </ListWrapper>
  );
};

export default LibraryBookmarkList;
